import { IS_USE_CENTIMETERS_SIZE_COUNTRY } from '@/constants/is-use-centimeters-size-country';
import { Decimal } from 'decimal.js';
import { useEffect, useRef, useState } from 'react';

export const useCheckCustomSize = props => {
  const { product, setShowMobileCustomSizeDialog } = props;

  const prevWaistConfirmedRef = useRef(false);
  const prevHeightConfirmedRef = useRef(false);
  const [showWaistTipDialog, setShowWaistTipDialog] = useState(false);
  const [showHeightTipDialog, setShowHeightTipDialog] = useState(false);
  const waistPromiseResolveRef = useRef(null);
  const heightTipPromiseResolveRef = useRef(null);
  const waitPromiseRef = useRef(null);
  const heightPromiseRef = useRef(null);

  useEffect(() => {
    return () => {
      waistPromiseResolveRef.current?.(false);
      waistPromiseResolveRef.current = null;
      waitPromiseRef.current = null;
      heightTipPromiseResolveRef.current?.(false);
      heightTipPromiseResolveRef.current = null;
      heightPromiseRef.current = null;
    };
  }, []);

  const confirmCustomSize = async formData => {
    const isCustomSize = formData?.Size?.label === 'customized_size';

    if (!isCustomSize) return true;

    const numericWaist = parseFloat(formData.waist);
    const isWaistInvalid = numericWaist > parseFloat(formData.bust) || numericWaist > parseFloat(formData.hip);
    const isFloorLengthSizeDress =
      product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value === 'Floor Length';
    const shippingTypeIsDresses = product.shipping_type === 'Dresses';
    const heightDifference = Decimal(parseFloat(formData.height))
      .minus(parseFloat(formData.hollowtoHem || 0))
      .toNumber();
    const isHeightInvalid =
      heightDifference < (IS_USE_CENTIMETERS_SIZE_COUNTRY ? 25.4 : 10) ||
      heightDifference > (IS_USE_CENTIMETERS_SIZE_COUNTRY ? 38.1 : 15);

    if (isWaistInvalid || (isFloorLengthSizeDress && shippingTypeIsDresses && isHeightInvalid)) {
      if (isWaistInvalid && !prevWaistConfirmedRef.current) {
        if (!waitPromiseRef.current) {
          waitPromiseRef.current = new Promise(resolve => {
            waistPromiseResolveRef.current = resolve;
          });
        }

        setShowWaistTipDialog(true);
      }

      if (isFloorLengthSizeDress && shippingTypeIsDresses && isHeightInvalid && !prevHeightConfirmedRef.current) {
        if (!heightPromiseRef.current) {
          heightPromiseRef.current = new Promise(resolve => {
            heightTipPromiseResolveRef.current = resolve;
          });
        }

        setShowHeightTipDialog(true);
      }

      let waistConfirmedByCustomer = prevWaistConfirmedRef.current || !!waitPromiseRef.current;
      let heightConfirmedByCustomer = prevHeightConfirmedRef.current || !!heightPromiseRef.current;

      const [waistConfirmed, heightConfirmed] = await Promise.all([
        waitPromiseRef.current ||
          new Promise(resolve => {
            resolve(true);
          }),
        heightPromiseRef.current ||
          new Promise(resolve => {
            resolve(true);
          }),
      ]);

      if (waistConfirmed && heightConfirmed) {
        prevWaistConfirmedRef.current = waistConfirmedByCustomer;
        prevHeightConfirmedRef.current = heightConfirmedByCustomer;
        return true;
      }

      if (waistConfirmed) prevWaistConfirmedRef.current = waistConfirmedByCustomer;

      if (heightConfirmed) prevHeightConfirmedRef.current = heightConfirmedByCustomer;

      setShowMobileCustomSizeDialog?.(true);

      return false;
    }

    return true;
  };

  const handleWaistTipDialogCancel = () => {
    setShowWaistTipDialog(false);
    waistPromiseResolveRef.current?.(false);
    waistPromiseResolveRef.current = null;
    waitPromiseRef.current = null;
  };

  const handleWaistTipDialogConfirm = () => {
    setShowWaistTipDialog(false);
    waistPromiseResolveRef.current?.(true);
    waistPromiseResolveRef.current = null;
    waitPromiseRef.current = null;
  };

  const handleHeightTipDialogCancel = () => {
    setShowHeightTipDialog(false);
    heightTipPromiseResolveRef.current?.(false);
    heightTipPromiseResolveRef.current = null;
    heightPromiseRef.current = null;
  };

  const handleHeightTipDialogConfirm = () => {
    setShowHeightTipDialog(false);
    heightTipPromiseResolveRef.current?.(true);
    heightTipPromiseResolveRef.current = null;
    heightPromiseRef.current = null;
  };

  return {
    handleWaistTipDialogCancel,
    handleWaistTipDialogConfirm,
    handleHeightTipDialogCancel,
    handleHeightTipDialogConfirm,
    prevWaistConfirmedRef,
    prevHeightConfirmedRef,
    showWaistTipDialog,
    setShowWaistTipDialog,
    showHeightTipDialog,
    setShowHeightTipDialog,
    confirmCustomSize,
  };
};
